/**
 * LabPublicBar — Barra do Lab de Testes para páginas públicas
 * ============================================================
 * Usada em páginas que NÃO utilizam BaseLayout (ex: /convite, /ativar).
 * Renderiza o LabFloatingPanel em posição fixed no rodapé.
 * Só ativa em DEV ou quando VITE_LAB_MODE=1.
 *
 * Props:
 *  autoOpen — se true, abre o painel automaticamente ao montar
 *             (usado quando URL contém ?lab-active=true)
 */

import React, { useState, useEffect, useRef } from 'react';
import { useLabContext } from './LabContext';

const isLabMode = import.meta.env.DEV || import.meta.env.VITE_LAB_MODE === '1';

interface LabPublicBarProps {
  /** Abre o painel automaticamente (ex: URL contém lab-active=true) */
  autoOpen?: boolean;
}

/**
 * LabPublicBar
 * Barra mínima fixa no rodapé que expõe o LabFloatingPanel
 * em rotas públicas sem BaseLayout.
 */
export function LabPublicBar({ autoOpen = false }: LabPublicBarProps) {
  const { isExecuting } = useLabContext();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [LabFloatingPanel, setLabFloatingPanel] = useState<React.ComponentType<any> | null>(null);
  const [labOpen, setLabOpen] = useState(false);
  const [, forceUpdate] = useState(0);
  const mountedRef = useRef(true);

  // Fecha o popup de roteiros quando a execução estiver ativa
  useEffect(() => {
    if (isExecuting) {
      setLabOpen(false);
    }
  }, [isExecuting]);

  // Importação lazy do LabFloatingPanel
  useEffect(() => {
    if (!isLabMode) return;
    mountedRef.current = true;
    import('./LabFloatingPanel').then((m) => {
      if (!mountedRef.current) return;
      setLabFloatingPanel(() => m.LabFloatingPanel);
      forceUpdate(n => n + 1);
      // Auto-abre se solicitado
      if (autoOpen) {
        setTimeout(() => setLabOpen(true), 800);
      }
    });
    return () => { mountedRef.current = false; };
  }, [autoOpen]);

  // Não renderiza nada fora do modo lab
  if (!isLabMode) return null;

  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    setLabOpen(v => !v);
  };

  return (
    <>
      {/* Barra fixa no rodapé */}
      <div
        id="ac-lab-public-bar"
        style={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 9999,
          height: 36,
          background: 'linear-gradient(90deg, #0f172a 0%, #1e1b4b 100%)',
          borderTop: '1px solid rgba(139,92,246,.4)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          padding: '0 14px',
          gap: 10,
          fontFamily: 'Inter, system-ui, sans-serif',
          boxShadow: '0 -2px 16px rgba(139,92,246,.15)',
        }}
      >
        {/* Ícone Lab */}
        <span style={{
          fontSize: '.68rem',
          color: '#94a3b8',
          letterSpacing: '.04em',
          fontWeight: 600,
          textTransform: 'uppercase',
          userSelect: 'none',
        }}>
          🧪 Lab de Testes
        </span>

        {/* Botão abrir/fechar */}
        <button
          id="ac-lab-public-bar-toggle"
          onClick={handleToggle}
          title={labOpen ? 'Fechar Lab de Testes' : 'Abrir Lab de Testes'}
          style={{
            height: 24,
            padding: '0 10px',
            borderRadius: 8,
            border: '1px solid rgba(139,92,246,.5)',
            background: labOpen
              ? 'rgba(139,92,246,.3)'
              : 'rgba(139,92,246,.12)',
            color: labOpen ? '#c4b5fd' : '#a78bfa',
            fontSize: '.72rem',
            fontWeight: 700,
            cursor: 'pointer',
            transition: 'all .15s',
            letterSpacing: '.02em',
          }}
        >
          {labOpen ? '✕ Fechar' : '▲ Roteiros'}
        </button>
      </div>

      {/* LabFloatingPanel renderizado acima da barra */}
      {labOpen && LabFloatingPanel && (
        <div
          id="ac-lab-public-panel"
          style={{
            position: 'fixed',
            bottom: 36,
            right: 0,
            zIndex: 9998,
            // Deixa o painel usar seu próprio tamanho/posição
          }}
        >
          <LabFloatingPanel
            position="bottom-right"
            embedded={false}
            open={labOpen}
            onClose={() => setLabOpen(false)}
          />
        </div>
      )}

      {/* Padding no body para não cobrir conteúdo */}
      <style>{`body { padding-bottom: 36px !important; }`}</style>
    </>
  );
}

export default LabPublicBar;
